import React from 'react';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  FlagOutlined,
  LoadingOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Popover, Typography } from 'antd';
import { Form, useEffectWithPrev } from '@owl-frontend/components';
import { Locale } from '../../context/AppProvider';
import useFeedbackHook from './useFeedbackHook';
import styles from './FeedbackComponents.module.scss';

interface FeedbackFormProps {
  formType: string;
  placeholder?: string;
}

const FeedbackForm: Form.MFC<FeedbackFormProps, { feedback: string }> = ({
  formType,
  placeholder,
  // RFF
  invalid,
  pristine,
  form,
  handleSubmit,
}) => {
  const { messages } = React.useContext(Locale);
  const sendFeedback = React.useCallback(() => {
    handleSubmit();
    form.restart();
  }, [form, handleSubmit]);

  return (
    <div className={styles.formContainer}>
      <form onSubmit={sendFeedback}>
        <div>
          <Typography.Text>
            {messages[`feedback.${formType}.description`]}
          </Typography.Text>
          <Form.InputManaged
            name="feedback"
            placeholder={
              placeholder ?? messages[`feedback.${formType}.placeholder`]
            }
            rows={3}
          />
        </div>
      </form>
      <div className={styles.buttonContainer}>
        <Button
          type="primary"
          onClick={sendFeedback}
          disabled={invalid || pristine}
        >
          {messages[`feedback.${formType}.submit`]}
          <ArrowRightOutlined />
        </Button>
      </div>
    </div>
  );
};

export const FeedbackFormManaged = Form.manage(FeedbackForm);

interface FeedbackComponentProps {
  type: string;
  formType: string;
  button: React.ReactNode;
  dossier_id?: string;
  data?: Record<string, string>;
  placeholder?: string;
}

const GenericFeedbackComponent: React.FC<FeedbackComponentProps> = ({
  type,
  formType,
  button,
  dossier_id,
  data = {},
  placeholder,
}) => {
  const [feedbackButton, setFeedbackButton] = React.useState(button);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [
    {
      actions: { addFeedback },
    },
    handler,
  ] = useFeedbackHook();

  const handleScroll = () => {
    setIsOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFeedbackSubmit = React.useCallback(
    (feedback) => {
      setIsSubmitted(true);
      setFeedbackButton(<LoadingOutlined />);
      handler.addFeedback({
        ...feedback,
        type,
        data,
        dossier_id,
      });
      setIsOpen(false);
    },
    [dossier_id, type, data, handler]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending' || !isSubmitted) {
        return;
      }
      if (addFeedback.status === 'fulfilled') {
        setFeedbackButton(<CheckCircleOutlined />);
        setTimeout(() => {
          setFeedbackButton(button);
          setIsSubmitted(false);
        }, 5000);
      }
    },
    [addFeedback.status, button, isSubmitted]
  );

  return (
    <Popover
      className={styles.popover}
      content={
        <FeedbackFormManaged
          formType={formType}
          onSubmit={handleFeedbackSubmit}
          placeholder={placeholder}
        />
      }
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      trigger="click"
    >
      <Avatar
        className={`${styles.feedbackButton} ${
          formType == 'general'
            ? styles.generalFeedbackButton
            : styles.specificFeedbackButton
        }`}
        size={28}
        icon={feedbackButton}
      />
    </Popover>
  );
};

export const GeneralFeedbackComponent: React.FC<{
  dossier_id: string;
}> = ({ dossier_id }) => {
  return (
    <GenericFeedbackComponent
      type="general"
      formType="general"
      dossier_id={dossier_id}
      button={<SmileOutlined style={{ fontSize: '14px' }} />}
    />
  );
};

interface SectionFeedbackComponentProps {
  type: string;
  dossier_id: string;
  data?: Record<string, string>;
  placeholder?: string;
}

export const SectionFeedbackComponent: React.FC<
  SectionFeedbackComponentProps
> = ({ type, dossier_id, data = {}, placeholder }) => {
  return (
    <GenericFeedbackComponent
      formType="specific"
      type={type}
      dossier_id={dossier_id}
      data={data}
      button={<FlagOutlined style={{ fontSize: '16px' }} />}
      placeholder={placeholder}
    />
  );
};
